import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Image,
  Platform,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbay.com/APIControllers';
const ASSET_BASE = 'https://collabbayadmin.com';

const Voucher = ({ route, navigation }) => {
  const CollabID = route?.params?.CollabID;

  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(null);
  const [postUpload, setPostUpload] = useState(null);
  const [contentMedia, setContentMedia] = useState(null);

  const [CustomerEmail, setCustomerEmail] = useState('');
  const [Price, setPrice] = useState('5');

  const [processing, setProcessing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);

  useEffect(() => {
    fetchVoucherInformation();
  }, [CollabID]);

  const normalizeAssetUrl = (value) => {
    if (!value) return null;

    const raw = String(value).trim();

    if (!raw) return null;

    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return raw;
    }

    if (raw.startsWith('//')) {
      return `https:${raw}`;
    }

    if (raw.startsWith('/')) {
      return `${ASSET_BASE}${raw}`;
    }

    return `${ASSET_BASE}/${raw}`;
  };

  const getInitials = (value) => {
    if (!value) return '?';

    const cleanValue = String(value).trim();

    if (!cleanValue) return '?';

    const parts = cleanValue.split(' ').filter(Boolean);

    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const getMediaTypeFromUrl = (url) => {
    const cleanUrl = String(url || '').split('?')[0].toLowerCase();

    if (
      cleanUrl.endsWith('.mp4') ||
      cleanUrl.endsWith('.mov') ||
      cleanUrl.endsWith('.webm') ||
      cleanUrl.endsWith('.m4v')
    ) {
      return 'video';
    }

    if (
      cleanUrl.endsWith('.jpg') ||
      cleanUrl.endsWith('.jpeg') ||
      cleanUrl.endsWith('.png') ||
      cleanUrl.endsWith('.gif') ||
      cleanUrl.endsWith('.webp') ||
      cleanUrl.endsWith('.avif')
    ) {
      return 'image';
    }

    return 'link';
  };

  const displayedMedia = useMemo(() => {
    if (contentMedia?.url) {
      const finalUrl = normalizeAssetUrl(contentMedia.url);

      return {
        url: finalUrl,
        type: contentMedia.type || getMediaTypeFromUrl(finalUrl),
      };
    }

    if (postUpload?.Image) {
      const finalUrl = normalizeAssetUrl(postUpload.Image);

      return {
        url: finalUrl,
        type: getMediaTypeFromUrl(finalUrl),
      };
    }

    return null;
  }, [contentMedia, postUpload]);

  const fetchVoucherInformation = async () => {
    if (!CollabID) {
      setLoading(false);
      Alert.alert('Missing voucher', 'No CollabID was provided.');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE}/FetchVoucherInformation.php?CollabID=${encodeURIComponent(CollabID)}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      const rawText = await response.text();

      let data;

      try {
        data = JSON.parse(rawText);
      } catch (parseError) {
        console.error('FetchVoucherInformation raw response:', rawText);
        Alert.alert('Server error', 'The voucher server returned invalid JSON.');
        return;
      }

      if (data.success) {
        setVoucher(data.voucher);
        setPostUpload(data.postUpload || null);
        setContentMedia(data.contentMedia || null);
      } else {
        Alert.alert('Error', data.message || 'Could not load voucher.');
      }
    } catch (error) {
      console.error('Fetch voucher error:', error);
      Alert.alert('Error', 'Something went wrong loading this voucher.');
    } finally {
      setLoading(false);
    }
  };

  const handleProxyPurchase = async () => {
    if (!voucher) {
      Alert.alert('Error', 'Voucher not loaded.');
      return;
    }

    if (!CustomerEmail || !Price) {
      Alert.alert('Missing fields', 'Please enter your email and price.');
      return;
    }

    if (!CustomerEmail.includes('@')) {
      Alert.alert('Invalid email', 'Please enter a valid email address.');
      return;
    }

    if (isNaN(Number(Price)) || Number(Price) <= 0) {
      Alert.alert('Invalid price', 'Price must be greater than 0.');
      return;
    }

    try {
      setProcessing(true);
      setPurchaseResult(null);

      const response = await fetch(`${API_BASE}/ProxyVoucherPurchase.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          CollabID,
          CustomerEmail,
          AmountPaid: Price,
        }),
      });

      const rawText = await response.text();

      let data;

      try {
        data = JSON.parse(rawText);
      } catch (parseError) {
        console.error('ProxyVoucherPurchase raw response:', rawText);
        Alert.alert('Server error', 'The purchase server returned invalid JSON.');
        return;
      }

      if (data.success) {
        setPurchaseResult(data.purchase);
        Alert.alert('Voucher ready', 'Your voucher details have been generated.');
        await fetchVoucherInformation();
      } else {
        Alert.alert('Error', data.message || 'Could not complete purchase.');
      }
    } catch (error) {
      console.error('Proxy purchase error:', error);
      Alert.alert('Error', 'Something went wrong completing this purchase.');
    } finally {
      setProcessing(false);
    }
  };

  const renderAvatar = (imageUrl, label, customStyle) => {
    const finalImageUrl = normalizeAssetUrl(imageUrl);

    return (
      <View style={[styles.avatarOuter, customStyle]}>
        {finalImageUrl ? (
          <Image
            source={{ uri: finalImageUrl }}
            style={styles.avatarImage}
            resizeMode="cover"
          />
        ) : (
          <View style={styles.avatarFallback}>
            <Text style={styles.avatarFallbackText}>{getInitials(label)}</Text>
          </View>
        )}
      </View>
    );
  };

  const renderHeroBackground = () => {
    if (!displayedMedia?.url) {
      return (
        <View style={styles.defaultHeroBackground}>
          <View style={styles.defaultHeroGlowOne} />
          <View style={styles.defaultHeroGlowTwo} />
        </View>
      );
    }

    if (displayedMedia.type === 'video' && Platform.OS === 'web') {
      return (
        <View style={styles.heroMediaLayer}>
          {React.createElement('video', {
            src: displayedMedia.url,
            autoPlay: true,
            muted: true,
            loop: true,
            playsInline: true,
            controls: false,
            style: {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            },
          })}
        </View>
      );
    }

    if (displayedMedia.type === 'image') {
      return (
        <Image
          source={{ uri: displayedMedia.url }}
          style={styles.heroBackgroundImage}
          resizeMode="cover"
        />
      );
    }

    return (
      <View style={styles.defaultHeroBackground}>
        <View style={styles.defaultHeroGlowOne} />
        <View style={styles.defaultHeroGlowTwo} />
      </View>
    );
  };

  const openContent = () => {
    if (displayedMedia?.url) {
      Linking.openURL(displayedMedia.url);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingPage}>
        <ActivityIndicator size="large" color="#111" />
        <Text style={styles.loadingText}>Loading voucher...</Text>
      </View>
    );
  }

  if (!voucher) {
    return (
      <View style={styles.loadingPage}>
        <Ionicons name="alert-circle-outline" size={34} color="#111" />
        <Text style={styles.errorTitle}>Voucher not found</Text>
        <Text style={styles.errorText}>This voucher could not be loaded.</Text>

        <TouchableOpacity
          style={styles.retryButton}
          activeOpacity={0.85}
          onPress={fetchVoucherInformation}
        >
          <Text style={styles.retryButtonText}>Try again</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const stockNumber = Number(voucher.Stock || 0);
  const soldOut = stockNumber <= 0;

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.topLine}>
        <TouchableOpacity
          style={styles.iconBackButton}
          activeOpacity={0.85}
          onPress={() => navigation?.goBack?.()}
        >
          <Ionicons name="chevron-back" size={20} color="#111" />
        </TouchableOpacity>

        <Text style={styles.topSupportText}>
          Support your favourite creators and local restaurants
        </Text>
      </View>

      <View style={styles.heroCard}>
        {renderHeroBackground()}

        <View style={styles.heroDarkOverlay} />
        <View style={styles.heroBottomFade} />
        <View style={styles.heroTopFade} />

        <View style={styles.heroContent}>
          <View style={styles.heroHeader}>
            <View style={styles.avatarStack}>
              {renderAvatar(voucher.BrandProfileImage, voucher.BrandName, styles.brandAvatar)}
              {renderAvatar(voucher.PromoterProfileImage, voucher.Promoter, styles.promoterAvatar)}
            </View>

            <View style={[styles.stockPill, soldOut && styles.soldOutPill]}>
              <View style={[styles.stockDot, soldOut && styles.soldOutDot]} />
              <Text style={[styles.stockPillText, soldOut && styles.soldOutText]}>
                {soldOut ? 'Sold out' : `${voucher.Stock} left`}
              </Text>
            </View>
          </View>

          <View style={styles.heroMainCopy}>
            <Text style={styles.heroVenue}>{voucher.BrandName}</Text>

            <Text style={styles.heroTitle}>
              {voucher.Discount}% off
            </Text>

            <Text style={styles.heroSubtitle}>
              when you spend £{voucher.OnSpend}
            </Text>

            <View style={styles.creatorLine}>
              <Text style={styles.creatorPrefix}>Shared by </Text>
              <Text style={styles.creatorName}>{voucher.Promoter}</Text>
              <View style={styles.blueTick}>
                <Ionicons name="checkmark" size={11} color="#fff" />
              </View>
            </View>
          </View>
        </View>
      </View>

      {displayedMedia?.url ? (
        <TouchableOpacity
          style={styles.contentProofCard}
          activeOpacity={0.88}
          onPress={openContent}
        >
          <View style={styles.contentProofIcon}>
            <Ionicons
              name={displayedMedia.type === 'video' ? 'play' : 'image-outline'}
              size={18}
              color="#111"
            />
          </View>

          <View style={styles.contentProofCopy}>
            <Text style={styles.contentProofTitle}>
              Creator content attached
            </Text>
            <Text style={styles.contentProofText}>
              Tap to open the original upload.
            </Text>
          </View>

          <Ionicons name="open-outline" size={20} color="#111" />
        </TouchableOpacity>
      ) : null}

      <View style={styles.checkoutCard}>
        <View style={styles.checkoutTopRow}>
          <View>
            <Text style={styles.checkoutEyebrow}>Voucher checkout</Text>
            <Text style={styles.checkoutTitle}>Complete your voucher</Text>
          </View>

          <View style={styles.discountBubble}>
            <Text style={styles.discountBubbleValue}>{voucher.Discount}%</Text>
            <Text style={styles.discountBubbleLabel}>off</Text>
          </View>
        </View>

        <View style={styles.paymentLookCard}>
          <View style={styles.paymentLookLeft}>
            <View style={styles.paymentIconCircle}>
              <Ionicons name="card-outline" size={19} color="#111" />
            </View>

            <View>
              <Text style={styles.paymentLookTitle}>Secure voucher payment</Text>
              <Text style={styles.paymentLookText}>
                Your code is generated after confirmation.
              </Text>
            </View>
          </View>

          <Ionicons name="lock-closed-outline" size={18} color="#777" />
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Email</Text>
          <View style={styles.inputShell}>
            <Ionicons name="mail-outline" size={19} color="#777" />
            <TextInput
              style={styles.input}
              value={CustomerEmail}
              onChangeText={setCustomerEmail}
              placeholder="customer@email.com"
              placeholderTextColor="#999"
              autoCapitalize="none"
              keyboardType="email-address"
            />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Price</Text>
          <View style={styles.inputShell}>
            <Text style={styles.currencySymbol}>£</Text>
            <TextInput
              style={styles.input}
              value={Price}
              onChangeText={setPrice}
              placeholder="5"
              placeholderTextColor="#999"
              keyboardType="decimal-pad"
            />
          </View>
        </View>

        <View style={styles.summaryBox}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Venue</Text>
            <Text style={styles.summaryValue}>{voucher.BrandName}</Text>
          </View>

          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Minimum spend</Text>
            <Text style={styles.summaryValue}>£{voucher.OnSpend}</Text>
          </View>

          <View style={styles.summaryRowLast}>
            <Text style={styles.summaryLabelStrong}>Voucher discount</Text>
            <Text style={styles.summaryValueStrong}>{voucher.Discount}% off</Text>
          </View>
        </View>

        <TouchableOpacity
          style={[
            styles.purchaseButton,
            (processing || soldOut) && styles.purchaseButtonDisabled,
          ]}
          activeOpacity={0.9}
          onPress={handleProxyPurchase}
          disabled={processing || soldOut}
        >
          {processing ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <>
              <Text style={styles.purchaseButtonText}>
                {soldOut ? 'Sold out' : 'Generate voucher'}
              </Text>
              <View style={styles.purchaseButtonIcon}>
                <Ionicons name="arrow-forward" size={18} color="#111" />
              </View>
            </>
          )}
        </TouchableOpacity>
      </View>

      {purchaseResult ? (
        <View style={styles.resultCard}>
          <View style={styles.successHeader}>
            <View style={styles.successIcon}>
              <Ionicons name="checkmark" size={22} color="#fff" />
            </View>

            <View style={styles.successCopy}>
              <Text style={styles.resultTitle}>Voucher generated</Text>
              <Text style={styles.resultSubtitle}>
                Save these details for redemption.
              </Text>
            </View>
          </View>

          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Voucher ID</Text>
            <Text selectable style={styles.resultValue}>
              {purchaseResult.VoucherID}
            </Text>
          </View>

          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Passcode</Text>
            <Text selectable style={styles.resultValue}>
              {purchaseResult.VoucherPasscode}
            </Text>
          </View>

          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Remaining stock</Text>
            <Text style={styles.resultValue}>
              {purchaseResult.RemainingStock}
            </Text>
          </View>

          <TouchableOpacity
            style={styles.resultLinkBox}
            activeOpacity={0.85}
            onPress={() => {
              if (purchaseResult.RedeemLink) {
                Linking.openURL(purchaseResult.RedeemLink);
              }
            }}
          >
            <View style={styles.resultLinkCopy}>
              <Text style={styles.resultLabel}>Redemption link</Text>
              <Text selectable style={styles.resultLink} numberOfLines={2}>
                {purchaseResult.RedeemLink}
              </Text>
            </View>

            <Ionicons name="open-outline" size={20} color="#111" />
          </TouchableOpacity>
        </View>
      ) : null}

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Support your creator, and local restaurants with collabbay ©
        </Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#f4f4f2',
  },
  pageContent: {
    padding: 18,
    paddingBottom: 70,
    width: '100%',
    maxWidth: 920,
    alignSelf: 'center',
  },

  loadingPage: {
    flex: 1,
    backgroundColor: '#f4f4f2',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontWeight: '800',
    color: '#666',
  },
  errorTitle: {
    marginTop: 10,
    fontSize: 22,
    fontWeight: '900',
    color: '#111',
  },
  errorText: {
    marginTop: 6,
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
  },
  retryButton: {
    marginTop: 18,
    backgroundColor: '#111',
    borderRadius: 999,
    paddingVertical: 12,
    paddingHorizontal: 18,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '900',
  },

  topLine: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
    gap: 10,
  },
  iconBackButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#e6e6e6',
  },
  topSupportText: {
    flex: 1,
    color: '#111',
    fontSize: 14,
    fontWeight: '900',
    lineHeight: 19,
  },

  heroCard: {
    minHeight: 520,
    borderRadius: 36,
    overflow: 'hidden',
    backgroundColor: '#111',
    marginBottom: 14,
    position: 'relative',
  },
  heroMediaLayer: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#111',
  },
  heroBackgroundImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  defaultHeroBackground: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#111',
  },
  defaultHeroGlowOne: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: 'rgba(253,240,93,0.22)',
    top: -80,
    right: -70,
  },
  defaultHeroGlowTwo: {
    position: 'absolute',
    width: 240,
    height: 240,
    borderRadius: 120,
    backgroundColor: 'rgba(255,255,255,0.08)',
    bottom: -80,
    left: -80,
  },
  heroDarkOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.34)',
  },
  heroTopFade: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 160,
    backgroundColor: 'rgba(0,0,0,0.25)',
  },
  heroBottomFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 300,
    backgroundColor: 'rgba(0,0,0,0.72)',
  },
  heroContent: {
    flex: 1,
    minHeight: 520,
    padding: 22,
    justifyContent: 'space-between',
  },
  heroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  avatarStack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarOuter: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#fff',
    padding: 3,
    borderWidth: 2,
    borderColor: '#fff',
  },
  brandAvatar: {
    zIndex: 1,
  },
  promoterAvatar: {
    marginLeft: -18,
    zIndex: 2,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 32,
    backgroundColor: '#ededed',
  },
  avatarFallback: {
    width: '100%',
    height: '100%',
    borderRadius: 32,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
  },
  stockPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(17,17,17,0.82)',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  soldOutPill: {
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderColor: 'rgba(255,255,255,0.4)',
  },
  stockDot: {
    width: 7,
    height: 7,
    borderRadius: 99,
    backgroundColor: '#FDF05D',
    marginRight: 7,
  },
  soldOutDot: {
    backgroundColor: '#999',
  },
  stockPillText: {
    fontSize: 13,
    fontWeight: '900',
    color: '#fff',
  },
  soldOutText: {
    color: '#111',
  },
  heroMainCopy: {
    paddingBottom: 8,
  },
  heroVenue: {
    color: '#FDF05D',
    fontSize: 15,
    fontWeight: '900',
    marginBottom: 8,
  },
  heroTitle: {
    color: '#fff',
    fontSize: 60,
    lineHeight: 64,
    fontWeight: '900',
    letterSpacing: -2,
  },
  heroSubtitle: {
    color: '#efefef',
    fontSize: 22,
    fontWeight: '800',
    marginTop: 6,
  },
  creatorLine: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 20,
  },
  creatorPrefix: {
    color: '#d8d8d8',
    fontSize: 16,
    fontWeight: '800',
  },
  creatorName: {
    color: '#fff',
    fontSize: 17,
    fontWeight: '900',
  },
  blueTick: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#1D9BF0',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 7,
    borderWidth: 1.5,
    borderColor: '#fff',
  },

  contentProofCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 14,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#e8e8e8',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  contentProofIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentProofCopy: {
    flex: 1,
  },
  contentProofTitle: {
    color: '#111',
    fontSize: 15,
    fontWeight: '900',
  },
  contentProofText: {
    marginTop: 2,
    color: '#777',
    fontSize: 13,
    fontWeight: '700',
  },

  checkoutCard: {
    backgroundColor: '#fff',
    borderRadius: 30,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e9e9e9',
  },
  checkoutTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 14,
  },
  checkoutEyebrow: {
    color: '#777',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 5,
  },
  checkoutTitle: {
    fontSize: 28,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.8,
  },
  discountBubble: {
    backgroundColor: '#111',
    borderRadius: 22,
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: 'center',
  },
  discountBubbleValue: {
    color: '#FDF05D',
    fontSize: 20,
    fontWeight: '900',
  },
  discountBubbleLabel: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '900',
    marginTop: -2,
  },

  paymentLookCard: {
    backgroundColor: '#f7f7f7',
    borderRadius: 22,
    padding: 14,
    borderWidth: 1,
    borderColor: '#ededed',
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  paymentLookLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  paymentIconCircle: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  paymentLookTitle: {
    color: '#111',
    fontSize: 15,
    fontWeight: '900',
  },
  paymentLookText: {
    marginTop: 2,
    color: '#777',
    fontSize: 12,
    fontWeight: '700',
  },

  inputGroup: {
    marginBottom: 13,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '900',
    color: '#111',
    marginBottom: 7,
  },
  inputShell: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f8f8',
    borderWidth: 1,
    borderColor: '#e2e2e2',
    borderRadius: 18,
    paddingHorizontal: 14,
    minHeight: 54,
  },
  currencySymbol: {
    color: '#777',
    fontSize: 18,
    fontWeight: '900',
    marginRight: 2,
  },
  input: {
    flex: 1,
    paddingVertical: 13,
    paddingHorizontal: 10,
    fontSize: 15,
    color: '#111',
    fontWeight: '700',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },

  summaryBox: {
    backgroundColor: '#fbfbfb',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#ededed',
    padding: 14,
    marginTop: 2,
    marginBottom: 14,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    paddingBottom: 10,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#ededed',
  },
  summaryRowLast: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  summaryLabel: {
    color: '#777',
    fontSize: 13,
    fontWeight: '800',
  },
  summaryValue: {
    flex: 1,
    color: '#111',
    fontSize: 13,
    fontWeight: '900',
    textAlign: 'right',
  },
  summaryLabelStrong: {
    color: '#111',
    fontSize: 14,
    fontWeight: '900',
  },
  summaryValueStrong: {
    color: '#111',
    fontSize: 14,
    fontWeight: '900',
    textAlign: 'right',
  },

  purchaseButton: {
    backgroundColor: '#111',
    borderRadius: 999,
    paddingVertical: 15,
    paddingHorizontal: 18,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    minHeight: 58,
  },
  purchaseButtonDisabled: {
    opacity: 0.55,
  },
  purchaseButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '900',
    marginRight: 12,
  },
  purchaseButtonIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
  },

  resultCard: {
    backgroundColor: '#ecf8ef',
    borderColor: '#c7ebd0',
    borderWidth: 1,
    borderRadius: 28,
    padding: 18,
    marginBottom: 16,
  },
  successHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
    gap: 10,
  },
  successIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#0a7d28',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successCopy: {
    flex: 1,
  },
  resultTitle: {
    fontSize: 20,
    fontWeight: '900',
    color: '#0a7d28',
  },
  resultSubtitle: {
    marginTop: 2,
    fontSize: 13,
    fontWeight: '700',
    color: '#43764f',
  },
  resultRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#d4efd9',
  },
  resultLabel: {
    fontSize: 13,
    fontWeight: '900',
    color: '#276b39',
  },
  resultValue: {
    flex: 1,
    fontSize: 13,
    fontWeight: '900',
    color: '#111',
    textAlign: 'right',
  },
  resultLinkBox: {
    marginTop: 12,
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  resultLinkCopy: {
    flex: 1,
  },
  resultLink: {
    marginTop: 5,
    fontSize: 13,
    fontWeight: '800',
    color: '#111',
  },

  footer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 18,
  },
  footerText: {
    textAlign: 'center',
    color: '#777',
    fontSize: 12,
    fontWeight: '800',
  },
});

export default Voucher;